import { COAProvider, Entity } from '../../types/entity';
import plural from '../../utilities/plural';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { ButtonV2 } from '@cimplico/components';
import { Fragment, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { MappingFormData } from './AccountsMappingForm';
import PreviewChart from '../entity-overview/entity-content/chart-of-accounts/PreviewChartModal';
import ChartProviderIcon from '../entity-overview/entity-content/chart-of-accounts/ChartProviderIcon';

type LinkingBannerProps = {
    entity: Entity;
    chartProvider?: COAProvider | null;
    isLoading?: boolean;
    isSaving?: boolean;
    bannerRef?: React.RefObject<HTMLDivElement>;
};

function LinkingBanner({ entity, chartProvider = null, isLoading = false, isSaving = false, bannerRef }: LinkingBannerProps): JSX.Element {
    const [previewChart, setPreviewChart] = useState(false);
    const context = useFormContext<MappingFormData>();
    const { mappings } = useWatch({ control: context.control });
    const unmapped = mappings?.filter((acc) => !acc.entityAccount)?.length ?? 0;

    const changes = context.formState.dirtyFields.mappings?.filter((m) => m).length || 0;

    return (
        <>
            {previewChart && <PreviewChart onClose={(): void => setPreviewChart(false)} entity={entity} provider={chartProvider} />}
            <div className="rounded-md border px-4 py-2 mb-2 flex items-center">
                <div className="mr-4">
                    <ChartProviderIcon provider={chartProvider} />
                </div>
                <ButtonV2 size="xs" type="secondary" label="Preview Chart" onClick={(): void => setPreviewChart(true)} />
                <div className="flex-1"></div>
                <div className="flex items-center gap-2">
                    {isLoading ? (
                        <div className="animate-pulse bg-gray-300 w-[150px] h-[20px] rounded"></div>
                    ) : (
                        <Fragment>
                            {unmapped > 0 ? (
                                <p className="text-sm text-red-600 flex items-center">
                                    <ExclamationCircleIcon className="h-5 w-5 mr-0.5" />
                                    {unmapped} {plural(unmapped, 'account is', 'accounts are')} unmapped
                                </p>
                            ) : (
                                <p className="text-sm text-green-600 flex items-center">
                                    <CheckCircleIcon className="h-5 w-5 mr-0.5" /> All accounts are mapped
                                </p>
                            )}
                        </Fragment>
                    )}
                    <span className="w-[1px] h-6 bg-gray-300" />
                    <ButtonV2
                        type="secondary"
                        label={`Confirm (${changes})`}
                        size="sm"
                        isDisabled={isLoading || isSaving || changes === 0}
                        isLoading={isSaving}
                        isSubmit
                        onClick={(): void => {
                            // Scroll to the top of the page
                            bannerRef?.current?.scrollIntoView({ block: 'end' });
                        }}
                    />
                </div>
            </div>
        </>
    );
}

export default LinkingBanner;
